/**
 * Designer Rugs -> Powerful Form Builder context bridge.
 *
 * Hidden values use the app's fixed-value tokens (`{{-dr.key-}}`). Visible
 * prefills are resolved from configured labels, never the app's generated ids.
 * The latter are regenerated by import/clone and are not a durable contract.
 */
(function () {
  if (window.__drAppFormBridgeLoaded) return;
  window.__drAppFormBridgeLoaded = true;

  const WRAPPER = '[data-app-placement][data-dr-app-form-ready]';
  const CONTROLS = 'input:not([type="hidden"]), select, textarea';
  const CONTRACTS = {
    "auction-remind": { complete: true, fields: [["text-1","Name"],["email","Email"]] },
    "piece-enquiry": { complete: true, singlePage: true, fields: [["text","Name"],["email","Email"],["text-1","Phone"],["textarea","Message"],["select","How did you hear about us?"]] },
    "preorder": { complete: true, singlePage: true, fields: [["text","Name"],["email","Email"],["text-1","Phone"],["textarea","Message"],["select","How did you hear about us?"]] },
    "custom-size-enquiry": { complete: true, singlePage: true, fields: [["text","Name"],["text-1","Phone"],["email","Email"],["country","Country"],["select","Shape"],["textarea","Message"],["select-1","How did you hear about us?"]] },
    "intl-enquiry-list": { complete: true, singlePage: true, fields: [["text","First Name"],["text-2","Last Name"],["email","Email"],["text-1","Phone / Mobile"],["country","Country"],["text-3","City"],["textarea","Message"],["select","How did you hear about us?"]] },
    'trade-enquiry': {
      complete: true,
      fields: [
        ['text', 'First Name'], ['text-1', 'Last Name'], ['text-2', 'Business Name'],
        ['email', 'Email'], ['phone', 'Phone / Mobile'], ['text-3', 'Address'],
        ['text-4', 'State'], ['text-5', 'Postcode'], ['select', 'Construction'],
        ['select-1', 'Custom Size (Shape)'], ['text-6', 'Custom Size (Dimensions)'],
        ['textarea', 'Message'], ['file2', 'Upload files']
      ]
    },
    'trade-enquiry-list': {
      complete: true,
      fields: [
        ['text', 'First Name'], ['text-1', 'Last Name'], ['text-2', 'Business Name'],
        ['email', 'Email'], ['phone', 'Phone or Mobile'], ['text-3', 'Address'],
        ['country', 'Country'], ['text-4', 'State / Province / Region'],
        ['text-5', 'Postcode / Zip Code'], ['textarea', 'Message'], ['file2', 'Upload files']
      ]
    },
    'trade-dashboard-enquiry': {
      complete: true,
      fields: [
        ['text', 'Name'], ['email', 'Email'], ['phone', 'Phone'],
        ['text-1', 'Showroom or your address'], ['text-2', 'Preferred times'], ['textarea', 'Message']
      ]
    },
    'trade-account-manager-enquiry': {
      complete: true,
      singlePage: true,
      fields: [['text', 'Name'], ['email', 'Email'], ['phone', 'Phone'], ['textarea', 'Message']]
    },
    'trade-application': {
      complete: true,
      singlePage: true,
      fields: [
        ['radio', 'Where is your business located?'],
        ['radio-1', 'Do you have an existing partnership with Designer Rugs?'],
        ['text', 'Business Name'], ['text-1', 'ABN'], ['text-2', 'First Name'],
        ['text-3', 'Last Name'], ['text-4', 'Job Title'], ['email', 'Email'],
        ['phone', 'Phone / Mobile'], ['text-5', 'Address'], ['text-6', 'State'],
        ['text-7', 'Post Code'], ['text-8', 'Province / Region'], ['text-9', 'Zip Code'],
        ['url', 'Website / URL'], ['file2', 'Upload files'], ['select', 'Industry'],
        ['select-1', 'Nature of Business'], ['checkbox', 'Newsletter']
      ]
    }
  };
  const HIDDEN_CONTRACTS = {
    "piece-enquiry": [["hidden","retail_product"],["hidden-1","retail_selection"],["hidden-2","retail_enquiry_type"]],
    "preorder": [["hidden","retail_product"],["hidden-1","retail_selection"],["hidden-2","retail_enquiry_type"]],
    "custom-size-enquiry": [["hidden","retail_product"],["hidden-1","retail_selection"],["hidden-2","retail_enquiry_type"],["hidden-3","retail_rep_email"]],
    "intl-enquiry-list": [["hidden","retail_enquiry_type"],["hidden-1","retail_count"],["hidden-2","retail_detected_country"],["hidden-3","retail_currency"],["hidden-4","retail_list"]],
    'trade-enquiry': [
      ['hidden', 'rug'], ['hidden-1', 'selection'], ['hidden-2', 'enquiry_type'],
      ['hidden-3', 'rep_name'], ['hidden-4', 'rep_email'], ['hidden-5', 'business']
    ],
    'trade-enquiry-list': [
      ['hidden', 'rep_name'], ['hidden-1', 'rep_email'], ['hidden-2', 'trade_account'],
      ['hidden-3', 'count'], ['hidden-4', 'list']
    ],
    'trade-dashboard-enquiry': [['hidden', 'rep_name'], ['hidden-1', 'rep_email']],
    'trade-account-manager-enquiry': [['hidden', 'rep_name'], ['hidden-1', 'rep_email'], ['hidden-2', 'business']],
    'trade-application': [['hidden', null, 'Trade account application']]
  };
  const APPLICATION_SECTIONS = [
    ['heading', '01.'],
    ['heading-1', '02.'],
    ['heading-2', '03.'],
    ['heading-3', '04.'],
    ['heading-4', '05.']
  ];
  const RECAPTCHA_V3 = 'input.globo-g-recaptchav3[name="recaptcha_response"][data-sitekey]';
  let applyQueued = false;

  function norm(value) {
    return String(value || '')
      .replace(/\*/g, ' ')
      .replace(/[^a-z0-9]+/gi, ' ')
      .trim()
      .toLowerCase()
      .replace(/\s+/g, ' ');
  }

  function warn(message, error) {
    try { console.warn('[dr-app-form] ' + message, error || ''); } catch (e) {}
  }

  function globalContext() {
    window.Globo = window.Globo || {};
    window.Globo.FormBuilder = window.Globo.FormBuilder || {};
    window.Globo.FormBuilder.dr = window.Globo.FormBuilder.dr || {};
    return window.Globo.FormBuilder.dr;
  }

  /**
   * Powerful Form Builder stores the Google key/version separately from each
   * form's generated CAPTCHA markup. If those records briefly diverge, its
   * storefront runtime can receive a v3 hidden field while the shop payload
   * still says v2; in that state it silently skips Google and submits an empty
   * token. Reconcile only that exact state and delegate all loading/execution
   * to the app's own renderer. Once the vendor payload reports v3 this is a
   * complete no-op.
   */
  function syncRecaptchaV3Runtime(wrapper) {
    if (!CONTRACTS[wrapper.dataset.formId]) return;
    const field = wrapper.querySelector(RECAPTCHA_V3);
    const fieldKey = String(field && field.getAttribute('data-sitekey') || '').trim();
    if (!field || !fieldKey || document.querySelector('.globo-g-recaptcha')) return;
    const builder = window.Globo && window.Globo.FormBuilder;
    const settings = builder && builder.shop && builder.shop.settings
      && builder.shop.settings.reCaptcha;
    const renderer = builder && builder.reCaptcha && builder.reCaptcha.renderReCaptcha;
    if (!settings || String(settings.recaptchaType || '').toLowerCase() === 'v3') return;
    if (String(settings.recaptchaType || '').toLowerCase() !== 'v2') return;
    if (String(settings.siteKey || '').trim() !== fieldKey || typeof renderer !== 'function') return;

    const previousType = settings.recaptchaType;
    try {
      settings.recaptchaType = 'v3';
      renderer.call(builder.reCaptcha);
      try { console.info('[dr-app-form] reconciled stale app CAPTCHA version metadata with v3 form markup'); } catch (e) {}
    } catch (error) {
      try { settings.recaptchaType = previousType; } catch (e) {}
      warn('unable to initialize the app v3 CAPTCHA renderer', error);
    }
  }

  function parse(wrapper) {
    if (wrapper.__drAppFormContext) return wrapper.__drAppFormContext;
    const source = wrapper.querySelector('[data-dr-app-form-context]');
    let context = { hidden: {}, prefill: {} };
    if (source) {
      try { context = JSON.parse(source.textContent); }
      catch (error) { warn('invalid context JSON for ' + (wrapper.dataset.formId || 'unknown placement'), error); }
    }
    context.hidden = context.hidden || {};
    context.prefill = context.prefill || {};
    wrapper.__drAppFormContext = context;
    return context;
  }

  function activate(wrapper) {
    const hidden = parse(wrapper).hidden;
    Object.keys(hidden).forEach(function (key) {
      if (hidden[key] !== undefined && hidden[key] !== null) globalContext()[key] = hidden[key];
    });
    applyHiddenInputs(wrapper, hidden);
    syncRecaptchaV3Runtime(wrapper);
  }

  function applyHiddenInputs(wrapper, values) {
    const contract = HIDDEN_CONTRACTS[wrapper.dataset.formId] || [];
    const inputs = Array.prototype.slice.call(wrapper.querySelectorAll('input[type="hidden"]'));
    contract.forEach(function (field) {
      const expected = field[0];
      const key = field[1];
      const input = inputs.find(function (candidate) { return hasExpectedId(candidate, expected); });
      if (!input || !key || values[key] === undefined || values[key] === null) return;
      if (String(input.value) !== String(values[key])) setNativeValue(input, values[key]);
    });
  }

  function elementBox(wrapper, expected, label) {
    const boxes = Array.prototype.slice.call(
      wrapper.querySelectorAll('.globo-form-control, .gfb__inputWrapper, [data-element-id]')
    ).filter(function (candidate) { return !candidate.matches(CONTROLS); });
    let box = boxes.find(function (candidate) { return hasExpectedId(candidate, expected); });
    if (box) return box;
    const controls = Array.prototype.slice.call(wrapper.querySelectorAll(CONTROLS));
    let control = controls.find(function (candidate) { return hasExpectedId(candidate, expected); });
    if (!control && label) control = findControl(wrapper, label);
    if (!control) return null;
    box = control.closest('.globo-form-control, .gfb__inputWrapper');
    if (box) return box;
    return control.parentElement && control.parentElement.closest('[data-element-id]');
  }

  function decorateApplication(wrapper) {
    if (wrapper.dataset.formId !== 'trade-application') return;
    const contract = CONTRACTS['trade-application'];
    contract.fields.forEach(function (field) {
      const box = elementBox(wrapper, field[0], field[1]);
      if (box) box.dataset.drAppField = field[0];
    });
  }

  function labelsFor(control) {
    const labels = [];
    if (control.labels) Array.prototype.forEach.call(control.labels, function (label) { labels.push(label.textContent); });
    const box = control.closest('.globo-form-control, .gfb__inputWrapper, [class*="form-control"]');
    if (box) {
      const label = box.querySelector('label, .globo-label, [class*="label"]');
      if (label) labels.push(label.textContent);
    }
    labels.push(control.getAttribute('aria-label'));
    labels.push(control.getAttribute('name'));
    labels.push(control.getAttribute('autocomplete'));
    labels.push(control.getAttribute('placeholder'));
    return labels.filter(Boolean).map(norm);
  }

  const AUTOCOMPLETE = {
    'first name': 'given name',
    'last name': 'family name',
    'business name': 'organization',
    'email': 'email',
    'phone mobile': 'tel',
    'phone or mobile': 'tel',
    'phone': 'tel',
    'name': 'name',
    'address': 'street address',
    'showroom or your address': 'street address',
    'country': 'country name',
    'state': 'address level1',
    'state province region': 'address level1',
    'postcode': 'postal code',
    'postcode zip': 'postal code'
  };

  function score(control, label) {
    const wanted = norm(label);
    const candidates = labelsFor(control);
    if (candidates.indexOf(wanted) !== -1) return 100;
    const autocomplete = AUTOCOMPLETE[wanted];
    if (autocomplete && candidates.indexOf(autocomplete) !== -1) return 80;
    /* Only use a contains fallback for labels longer than "Name". Exact label
       matching keeps Name away from First Name and Business Name. */
    if (wanted.length > 5 && candidates.some(function (x) { return x.indexOf(wanted) !== -1; })) return 40;
    return 0;
  }

  function findControl(wrapper, label) {
    let best = null;
    wrapper.querySelectorAll(CONTROLS).forEach(function (control) {
      const points = score(control, label);
      if (points && (!best || points > best.points)) best = { control: control, points: points };
    });
    return best && best.control;
  }

  function hasExpectedId(control, expected) {
    const attrs = ['id', 'name', 'data-id', 'data-field-id', 'data-element-id']
      .map(function (name) { return control.getAttribute(name) || ''; })
      .filter(Boolean);
    return attrs.some(function (value) {
      return value === expected || value.endsWith('-' + expected) || value.endsWith('_' + expected)
        || value.indexOf('[' + expected + ']') !== -1;
    });
  }

  function previewMode() {
    if (window.Shopify && window.Shopify.designMode) return true;
    try { return new URLSearchParams(window.location.search).has('preview_theme_id'); }
    catch (e) { return false; }
  }

  function showContractError(wrapper, message) {
    if (!previewMode()) return;
    let alert = wrapper.querySelector('[data-dr-app-contract-error]');
    if (!alert) {
      alert = document.createElement('p');
      alert.dataset.drAppContractError = 'true';
      alert.className = 'dr-app-form__contract-error';
      alert.setAttribute('role', 'alert');
      wrapper.insertBefore(alert, wrapper.firstChild);
    }
    alert.textContent = message;
  }

  function validateContract(wrapper) {
    const contract = CONTRACTS[wrapper.dataset.formId];
    if (!contract || !wrapper.querySelector('form')) return;
    /* The app inserts the form shell before its controls. Give that incremental
       render one beat so preview diagnostics report real drift, not a transient
       half-built DOM. */
    if (!wrapper.__drContractReadyAt) {
      wrapper.__drContractReadyAt = Date.now() + 1000;
      setTimeout(function () { validateContract(wrapper); }, 1050);
      return;
    }
    if (Date.now() < wrapper.__drContractReadyAt) return;
    const missing = [];
    const drifted = [];
    contract.fields.forEach(function (field) {
      const expected = field[0];
      const label = field[1];
      const box = elementBox(wrapper, expected, label);
      const control = box && box.querySelector(CONTROLS);
      if (!box || !control) {
        if (contract.complete) missing.push(label + ' (' + expected + ')');
        return;
      }
      if (!hasExpectedId(box, expected) && !hasExpectedId(control, expected)) {
        drifted.push(label + ' (expected ' + expected + ')');
      }
    });
    const hiddenInputs = Array.prototype.slice.call(wrapper.querySelectorAll('input[type="hidden"]'));
    (HIDDEN_CONTRACTS[wrapper.dataset.formId] || []).forEach(function (field) {
      const input = hiddenInputs.find(function (candidate) { return hasExpectedId(candidate, field[0]); });
      if (!input) {
        missing.push('hidden field (' + field[0] + ')');
      } else if (field[2] !== undefined && String(input.value) !== String(field[2])) {
        drifted.push('hidden field value (' + field[0] + ')');
      }
    });
    if (contract.singlePage) {
      let panels = Array.prototype.slice.call(wrapper.querySelectorAll('.panel[data-step]'));
      if (!panels.length) panels = Array.prototype.slice.call(wrapper.querySelectorAll('.wizard__content > .panel'));
      if (panels.length > 1) drifted.push('multi-step layout (expected one continuous page)');
      const next = wrapper.querySelector('button.action.next:not(.submit)');
      const previous = wrapper.querySelector('button.action.previous');
      if (next || previous) drifted.push('wizard navigation (expected one continuous page)');
      (wrapper.dataset.formId === 'trade-application' ? APPLICATION_SECTIONS : []).forEach(function (section) {
        const headingBox = elementBox(wrapper, section[0], '');
        const heading = headingBox && headingBox.querySelector('.globo-heading, h1, h2, h3, h4, h5, h6');
        if (!heading) missing.push('section numeral ' + section[1] + ' (' + section[0] + ')');
        else if (String(heading.textContent || '').trim() !== section[1]) {
          drifted.push('section numeral ' + section[1] + ' (' + section[0] + ')');
        }
      });
    }
    const problems = missing.concat(drifted);
    if (!problems.length) {
      wrapper.dataset.drAppContract = 'ok';
      const old = wrapper.querySelector('[data-dr-app-contract-error]');
      if (old) old.remove();
      return;
    }
    const signature = problems.join('|');
    wrapper.dataset.drAppContract = 'drift';
    if (wrapper.__drContractWarning !== signature) {
      wrapper.__drContractWarning = signature;
      const message = 'Form field contract drift for ' + wrapper.dataset.formId + ': ' + problems.join(', ');
      try { console.error('[dr-app-form] ' + message); } catch (e) {}
      showContractError(wrapper, message + '. Do not publish this theme until the form ids/labels match.');
    }
  }

  function setNativeValue(control, value) {
    if (control.tagName === 'SELECT') {
      const wanted = norm(value);
      const options = Array.prototype.slice.call(control.options);
      let option = options.find(function (x) { return norm(x.value) === wanted || norm(x.textContent) === wanted; });
      if (!option && wanted) {
        const stem = wanted.replace(/ed\b/g, '').replace(/ing\b/g, '').replace(/\s+/g, '');
        option = options.find(function (x) {
          const candidate = norm(x.value || x.textContent).replace(/ed\b/g, '').replace(/ing\b/g, '').replace(/\s+/g, '');
          return candidate && (candidate.indexOf(stem) !== -1 || stem.indexOf(candidate) !== -1);
        });
      }
      if (!option) return false;
      control.value = option.value;
    } else {
      const descriptor = Object.getOwnPropertyDescriptor(
        control.tagName === 'TEXTAREA' ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype,
        'value'
      );
      if (descriptor && descriptor.set) descriptor.set.call(control, value);
      else control.value = value;
    }
    control.dispatchEvent(new Event('input', { bubbles: true }));
    control.dispatchEvent(new Event('change', { bubbles: true }));
    return true;
  }

  function empty(control) {
    if (control.tagName === 'SELECT') return !control.value;
    return !String(control.value || '').trim();
  }

  function apply(wrapper) {
    activate(wrapper);
    if (wrapper.dataset.formId === 'auction-remind') {
      ['Name', 'Email'].forEach(function (label) {
        const input = findControl(wrapper, label);
        if (!input) return;
        input.required = true;
        input.autocomplete = label === 'Email' ? 'email' : 'name';
        if (label === 'Email') input.type = 'email';
      });
    }
    decorateApplication(wrapper);
    const prefill = parse(wrapper).prefill;
    Object.keys(prefill).forEach(function (label) {
      const value = prefill[label];
      if (value === undefined || value === null || String(value).trim() === '') return;
      const control = findControl(wrapper, label);
      if (!control || control.dataset.drUserEdited === 'true') return;
      /* Override an app-selected first option on first render (notably Country),
         then leave the control alone unless the context itself changes. */
      if (control.dataset.drPrefillValue === String(value) && !empty(control)) return;
      if (setNativeValue(control, value)) {
        control.dataset.drPrefilled = 'true';
        control.dataset.drPrefillValue = String(value);
      }
    });
    validateContract(wrapper);
  }

  function init(wrapper) {
    if (wrapper.dataset.drAppFormReady) return;
    wrapper.dataset.drAppFormReady = 'true';
    parse(wrapper);
    activate(wrapper);
    ['focusin', 'pointerdown', 'submit'].forEach(function (eventName) {
      wrapper.addEventListener(eventName, function () { activate(wrapper); }, true);
    });
    ['input', 'change'].forEach(function (eventName) {
      wrapper.addEventListener(eventName, function (event) {
        if (event.isTrusted && event.target && event.target.matches && event.target.matches(CONTROLS)) {
          event.target.dataset.drUserEdited = 'true';
        }
      }, true);
    });
    apply(wrapper);
  }

  function applyAll() {
    document.querySelectorAll('[data-app-placement]').forEach(init);
    document.querySelectorAll(WRAPPER).forEach(apply);
  }

  function queueApply() {
    if (applyQueued) return;
    applyQueued = true;
    requestAnimationFrame(function () { applyQueued = false; applyAll(); });
  }

  function resolve(target) {
    if (target && target.nodeType === 1) return target;
    if (!target) return null;
    return document.querySelector('[data-app-placement][data-form-id="' + String(target).replace(/"/g, '\\"') + '"]');
  }

  window.DRAppForms = {
    update: function (target, patch) {
      const wrapper = resolve(target);
      if (!wrapper) return false;
      const context = parse(wrapper);
      if (patch && patch.hidden) Object.assign(context.hidden, patch.hidden);
      if (patch && patch.prefill) Object.assign(context.prefill, patch.prefill);
      if (patch && patch.enquiryType) wrapper.dataset.enquiryType = patch.enquiryType;
      apply(wrapper);
      return true;
    },
    apply: function (target) {
      const wrapper = resolve(target);
      if (!wrapper) return false;
      apply(wrapper);
      return true;
    }
  };

  const observer = new MutationObserver(queueApply);
  function boot() {
    applyAll();
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
  document.addEventListener('shopify:section:load', queueApply);
  document.addEventListener('globo.formbuilder.form.loaded', queueApply);
})();

/**
 * Refresh v3 at the vendor's validated-send boundary, not at page load.
 * This is intentionally pinned to the reviewed Powerful Form Builder runtime.
 * Unknown forms, versions, keys, endpoints and replacement runtimes are not
 * intercepted. Google verification and the vendor POST/response engine remain
 * unchanged. No retry sends are made by this bridge.
 */
(function () {
  if (window.__drFreshCaptchaGateLoaded) return;
  window.__drFreshCaptchaGateLoaded = true;

  const IDS = new Set(['162127', '162821', '162822', '162823', '162830', '163062', '163064', '163068', '163103']);
  const FIELD = 'input.globo-g-recaptchav3[name="recaptcha_response"][data-sitekey]';
  const ACTION = 'globoformbuilder';
  const TIMEOUT_MS = 15000;
  const pending = new WeakMap();
  const runtimes = new WeakMap();
  const EXPECTED = {
    xhr: [2309, 'ec5f58f2'], handleSubmit: [10972, '822e1e41'],
    renderReCaptcha: [1889, 'f8a3fa83'], resetReCaptcha: [422, 'df5a774b']
  };

  // Non-security drift detector: length + FNV-1a of the exact reviewed source.
  // This does not validate CAPTCHA tokens; only Google's backend does that.
  function matches(fn, name) {
    if (typeof fn !== 'function') return false;
    const source = Function.prototype.toString.call(fn), expected = EXPECTED[name];
    if (source.length !== expected[0]) return false;
    let hash = 2166136261;
    for (let i = 0; i < source.length; i++) { hash ^= source.charCodeAt(i); hash = Math.imul(hash, 16777619); }
    return (hash >>> 0).toString(16) === expected[1];
  }

  function contract(builder, form) {
    if (!(form instanceof HTMLFormElement) || !form.closest('[data-app-placement]')) return null;
    const id = form.getAttribute('data-id');
    if (!IDS.has(id) || String(form.method).toLowerCase() !== 'post') return null;
    let url;
    try { url = new URL(form.action, location.href); } catch (_) { return null; }
    if (url.origin !== 'https://app.powerfulform.com' || url.pathname !== '/api/front/form/' + id + '/send'
      || url.search || url.hash) return null;
    const settings = builder.shop && builder.shop.settings && builder.shop.settings.reCaptcha;
    const config = builder.forms && builder.forms[id];
    if (!settings || settings.recaptchaType !== 'v3' || !config || !config.reCaptcha
      || config.reCaptcha.enable !== true || config.reCaptcha.captchaType !== 'reCaptcha') return null;
    const fields = form.querySelectorAll(FIELD);
    if (fields.length !== 1 || form.querySelector('.globo-g-recaptcha, .globo-h-captcha')) return null;
    const field = fields[0], key = String(field.getAttribute('data-sitekey') || '').trim();
    if (!key || String(settings.siteKey || '').trim() !== key) return null;
    return { id: id, field: field, key: key, action: form.action };
  }

  function recognized(builder, original) {
    return builder && matches(original, 'xhr') && matches(builder.handleSubmit, 'handleSubmit')
      && builder.reCaptcha && matches(builder.reCaptcha.renderReCaptcha, 'renderReCaptcha')
      && matches(builder.reCaptcha.resetReCaptcha, 'resetReCaptcha');
  }

  function failure() {
    // Feed the normal vendor error path: visible message, loading/button
    // recovery and its existing CAPTCHA reset, but never a form POST.
    return { success: 'false', errors: ['Security verification could not be completed. Please try again.'] };
  }

  function freshToken(key) {
    return new Promise(function (resolve, reject) {
      let done = false;
      const finish = function (error, token) {
        if (done) return;
        done = true;
        clearTimeout(timer);
        if (error) reject(error); else resolve(token);
      };
      const timer = setTimeout(function () { finish(new Error('captcha timeout')); }, TIMEOUT_MS);
      const google = window.grecaptcha;
      if (!google || typeof google.ready !== 'function' || typeof google.execute !== 'function') {
        finish(new Error('captcha unavailable')); return;
      }
      try {
        google.ready(function () {
          if (done) return;
          try {
            Promise.resolve(google.execute(key, { action: ACTION })).then(function (token) {
              if (typeof token !== 'string' || !token.trim()) finish(new Error('captcha empty'));
              else finish(null, token);
            }, function () { finish(new Error('captcha rejected')); });
          } catch (_) { finish(new Error('captcha execution failed')); }
        });
      } catch (_) { finish(new Error('captcha readiness failed')); }
    });
  }

  function install(builder) {
    const old = runtimes.get(builder);
    if (old && builder.xhr === old.wrapped) {
      if (recognized(builder, old.original)) return old;
      builder.xhr = old.original;
      return null;
    }
    if (old && builder.xhr === old.original && recognized(builder, old.original)) {
      builder.xhr = old.wrapped;
      return old;
    }
    if (!recognized(builder, builder.xhr)) return null;
    const original = builder.xhr;
    const record = { original: original, wrapped: null };
    record.wrapped = function () {
      const receiver = this, args = arguments, form = args[0];
      const checked = receiver === builder && contract(builder, form);
      if (!checked || !recognized(builder, original)) return original.apply(receiver, args);
      if (pending.has(form)) return pending.get(form);
      const work = freshToken(checked.key).then(function (token) {
        const current = contract(builder, form);
        if (!window.Globo || window.Globo.FormBuilder !== builder || !form.isConnected || !current
          || current.field !== checked.field || current.key !== checked.key || current.action !== checked.action
          || !recognized(builder, original) || (builder.xhr !== record.wrapped && builder.xhr !== original)) return failure();
        // Token assignment and vendor FormData construction happen in the same
        // JS turn. A parallel form/reset cannot overwrite this token between
        // those operations. Only this form receives this newly issued token.
        checked.field.value = token;
        return original.apply(receiver, args);
      }, failure);
      const tracked = work.finally(function () { if (pending.get(form) === tracked) pending.delete(form); });
      pending.set(form, tracked);
      return tracked;
    };
    runtimes.set(builder, record);
    builder.xhr = record.wrapped;
    return record;
  }

  // A second normal submit while either token issuance or vendor xhr is in
  // flight is cancelled before document-level analytics/context handlers.
  // Different forms remain independent; no page-wide lock is used.
  window.addEventListener('submit', function (event) {
    if (!pending.has(event.target)) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);

  // The reviewed vendor dispatches this synchronously after validation and
  // before xhr. Installing here also handles its legitimate init reassigning
  // the exact original xhr. We never replace an unknown third-party wrapper.
  document.addEventListener('globo.formbuilder.form.beforeSubmit', function (event) {
    const builder = window.Globo && window.Globo.FormBuilder;
    if (!builder || !event.detail || !contract(builder, event.detail.form)) return;
    install(builder);
  });
})();
